import { Breadcrumb } from '../components/marketing/Breadcrumb';
import { SEO } from '../components/SEO';

export function ContactPage(): JSX.Element {
  return (
    <>
      <SEO
        title="Contact"
        description="Get in touch with the Resida team. We're here to help independent landlords get started and answer questions about the platform."
        path="/contact"
      />

      {/* Hero */}
      <section className="pt-12 pb-16 bg-[var(--landing-bg)]">
        <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb
            items={[
              { label: 'Home', href: '/' },
              { label: 'Contact' },
            ]}
          />
          <div className="max-w-3xl">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--landing-text-primary)] leading-tight mb-4">
              Get in touch
            </h1>
            <p className="text-lg text-[var(--landing-text-muted)]">
              Have a question about Resida? We&apos;re here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact options */}
      <section className="py-[var(--landing-section-py)] bg-[var(--landing-surface-alt)]">
        <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Email */}
              <div className="bg-[var(--landing-surface)] rounded-[var(--landing-radius)] p-6 border border-[var(--landing-border)]">
                <h2 className="text-lg font-semibold text-[var(--landing-text-primary)] mb-2">
                  Email us
                </h2>
                <p className="text-sm text-[var(--landing-text-muted)] mb-4">
                  General questions, feedback, or partnership inquiries.
                </p>
                <a
                  href="mailto:support@resida.me"
                  className="text-[var(--landing-accent-text)] font-medium hover:underline"
                >
                  support@resida.me
                </a>
              </div>

              {/* Response time */}
              <div className="bg-[var(--landing-surface)] rounded-[var(--landing-radius)] p-6 border border-[var(--landing-border)]">
                <h2 className="text-lg font-semibold text-[var(--landing-text-primary)] mb-2">
                  Response time
                </h2>
                <p className="text-sm text-[var(--landing-text-muted)]">
                  We typically respond within one business day. For existing
                  customers, use the in-app chat for faster support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Common topics */}
      <section className="py-[var(--landing-section-py)] bg-[var(--landing-bg)]">
        <div className="max-w-[var(--landing-container-max)] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--landing-text-primary)] text-center mb-8">
              Common topics
            </h2>
            <div className="space-y-4">
              {[
                {
                  topic: 'Getting started',
                  description:
                    'Questions about setup, importing properties, or configuring your account.',
                },
                {
                  topic: 'Billing and pricing',
                  description:
                    'Questions about the free trial, subscription, or payment processing fees.',
                },
                {
                  topic: 'Feature requests',
                  description:
                    'Ideas for improving Resida? We read every suggestion and prioritize based on landlord feedback.',
                },
                {
                  topic: 'Bug reports',
                  description:
                    'Found something that isn\'t working right? Include what you were doing and any error messages.',
                },
              ].map((item) => (
                <div
                  key={item.topic}
                  className="flex items-start gap-3 text-sm"
                >
                  <span
                    className="text-[var(--landing-accent-text)] font-bold flex-shrink-0 mt-0.5"
                    aria-hidden="true"
                  >
                    &#10003;
                  </span>
                  <div>
                    <span className="font-medium text-[var(--landing-text-primary)]">
                      {item.topic}
                    </span>
                    <span className="text-[var(--landing-text-muted)]">
                      {' '}
                      &mdash; {item.description}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
